<footer class="site-footer">
  <div class="container">
    <!--
      Trust band. Everything listed here has to be something we can evidence on
      request. No purchased seals, no invented certifications.
    -->
    <section class="footer-trust" aria-labelledby="footer-trust-title">
      <div class="footer-trust-head">
        <h2 id="footer-trust-title">Why teams trust us with this data</h2>
        <p>
          Deceased records touch people at their most vulnerable moment. Here is exactly how we
          handle that, and every point below is something we will evidence in writing.
        </p>
      </div>
      <ul class="footer-trust-grid">
        @for (badge of trustBadges; track badge.title) {
          <li class="footer-trust-item">
            <span class="footer-trust-icon" aria-hidden="true" [innerHTML]="iconFor(badge.icon)"></span>
            <div>
              <span class="footer-trust-title">{{ badge.title }}</span>
              <span class="footer-trust-detail">{{ badge.detail }}</span>
            </div>
          </li>
        }
      </ul>
      <p class="footer-trust-note">
        SOC 2 Type II is in progress and we do not claim a report we do not hold.
        <a [routerLink]="[routes.security.url]">Read the full trust center</a>
      </p>
    </section>

    <div class="footer-top">
      <div class="footer-brand">
        <div class="footer-logo">
          <span class="footer-mark" aria-hidden="true">
            <svg viewBox="0 0 64 64" width="40" height="40">
              <defs>
                <linearGradient id="ftrShield" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stop-color="#8577e0" />
                  <stop offset="1" stop-color="#5042a9" />
                </linearGradient>
              </defs>
              <rect width="64" height="64" rx="14" fill="url(#ftrShield)" />
              <path d="M32 12 L48 18 V32 C48 42 41 49 32 52 C23 49 16 42 16 32 V18 Z"
                fill="#ffffff" fill-opacity="0.14" stroke="#fbdc70" stroke-width="2"
                stroke-linejoin="round" />
              <path d="M24 32 L30 38 L41 25" fill="none" stroke="#ffffff" stroke-width="4.5"
                stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
          <div>
            <span class="footer-name">VerifyDeceased</span>
            <a class="footer-parent" href="https://leadcruncher.com" rel="noopener" target="_blank">a LeadCruncher product</a>
          </div>
        </div>
        <p class="footer-tagline">
          Confirm whether a consumer is deceased with fresh nationwide death data. Built for banks,
          lenders and enterprise fraud teams that need to act on the truth, fast.
        </p>
        <div class="footer-social">
          <a [href]="site.social.linkedin" aria-label="LinkedIn" rel="noopener" target="_blank">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M4.98 3.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5ZM3 9h4v12H3V9Zm7 0h3.8v1.7h.05c.53-1 1.83-2.05 3.76-2.05C21.4 8.65 22 11 22 14.1V21h-4v-6.1c0-1.45-.03-3.3-2-3.3-2.01 0-2.32 1.57-2.32 3.2V21H10V9Z" />
            </svg>
          </a>
          <a [href]="site.social.x" aria-label="X" rel="noopener" target="_blank">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M18.9 2H22l-7.6 8.7L23.3 22h-6.9l-5.4-7-6.2 7H1.6l8.1-9.3L.9 2h7.1l4.9 6.5L18.9 2Zm-2.4 18h1.9L7.6 4H5.6l10.9 16Z" />
            </svg>
          </a>
          <a [href]="site.social.facebook" aria-label="Facebook" rel="noopener" target="_blank">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0 0 22 12Z" />
            </svg>
          </a>
        </div>
      </div>

      <nav class="footer-columns" aria-label="Footer">
        <div class="footer-column">
          <span class="footer-heading">Product</span>
          <a [routerLink]="[routes.howItWorks.url]">How it works</a>
          <a [routerLink]="[routes.pricing.url]">Pricing</a>
          <a [routerLink]="[routes.developers.url]">Developers and API</a>
          <a [routerLink]="[routes.security.url]">Security and compliance</a>
          <a [routerLink]="[routes.resources.url]">Guides and research</a>
        </div>

        <div class="footer-column">
          <span class="footer-heading">Solutions</span>
          <a [routerLink]="[routes.solutionsBanks.url]">Banks and credit unions</a>
          <a [routerLink]="[routes.solutionsLenders.url]">Lenders and card issuers</a>
          <a [routerLink]="[routes.solutionsEmployers.url]">Employers</a>
          <a [routerLink]="[routes.solutionsInsurance.url]">Insurance carriers</a>
          <a [routerLink]="[routes.solutionsGovernment.url]">Government</a>
          <a [routerLink]="[routes.solutionsCollections.url]">Collections</a>
        </div>

        <div class="footer-column">
          <span class="footer-heading">Company</span>
          <a [routerLink]="[routes.about.url]">About us</a>
          <a [routerLink]="[routes.faq.url]">FAQ</a>
          <a [routerLink]="[routes.contact.url]">Contact sales</a>
          <a [routerLink]="[routes.bookACall.url]">Book a call</a>
          <a [routerLink]="[routes.feedback.url]">Give feedback</a>
          <a href="https://leadcruncher.com" rel="noopener" target="_blank">LeadCruncher</a>
        </div>

        <div class="footer-column">
          <span class="footer-heading">Get in touch</span>
          <a [href]="site.phoneHref">{{ site.phone }}</a>
          <a [href]="'mailto:' + site.email">{{ site.email }}</a>
          <span class="footer-address">{{ site.address }}</span>
        </div>
      </nav>
    </div>

    <div class="footer-coverage">
      <span class="footer-heading">Nationwide coverage</span>
      <p>
        VerifyDeceased provides confirmed deceased consumer records across all 50 states and the
        District of Columbia, including
        @for (state of states; track state; let last = $last) {
          {{ state }}{{ last ? "." : ", " }}
        }
      </p>
    </div>
  </div>

  <div class="footer-bottom">
    <div class="container footer-bottom-inner">
      <span>&copy; {{ year }} LeadCruncher. All rights reserved. VerifyDeceased is a LeadCruncher product.</span>
      <nav class="footer-legal" aria-label="Legal">
        <a [routerLink]="[routes.privacy.url]">Privacy Policy</a>
        <a [routerLink]="[routes.terms.url]">Terms of Service</a>
      </nav>
    </div>
  </div>
</footer>
